import * as React from "react"

/*
 * The bottom sheets' swipe-down-to-dismiss, in its own module since RB-40: the phone's
 * full-screen menu is not a Radix sheet (it must leave the bar under it tappable) but
 * dismisses with the same gesture, and a component file that also exports a hook breaks
 * fast refresh. Moved verbatim from sheet.tsx.
 */

/**
 * Touch targets inside a sheet that own their own drag gesture.
 *
 * A bottom sheet dismisses on a downward drag, which is indistinguishable from
 * dragging a focal-point crosshair down an image or pulling a slider — both
 * live inside sheets today. The inner handler calling `preventDefault()` does
 * not help: preventDefault is not stopPropagation, so the React synthetic event
 * still bubbles to SheetContent and the sheet slides away under the finger.
 *
 * Range inputs are matched implicitly because they are a global hazard and
 * every one of them means the same thing; anything else opts out by putting
 * `data-no-swipe-dismiss` on (or above) the draggable element.
 */
const SWIPE_OPT_OUT_SELECTOR = '[data-no-swipe-dismiss],input[type="range"]'

/**
 * True when anything between the touch target and the sheet panel (inclusive) is
 * scrolled away from its top.
 *
 * A bottom sheet's close gesture is a downward drag — and so is scrolling a long form
 * back up to its beginning. With the content scrolled, the finger is asking for the
 * content, not the sheet; only once the content is at its top does a further pull
 * mean "close" (the iOS sheet convention). Without this, scrolling back up the
 * Add-someone form dismissed it after 100 px and dropped everything typed (IN-276).
 *
 * Walks the ancestors rather than looking up one known container, because every
 * sheet lays out its own scroll area — the panel itself, or a flex child inside it.
 */
function startsInScrolledContent(target: HTMLElement | null, panel: HTMLElement): boolean {
  // A portaled child (a dropdown) bubbles its React touch events here without being in
  // the panel's DOM; walking up from it would reach the page behind the sheet.
  if (!target || !panel.contains(target)) return false
  for (let el: HTMLElement | null = target; el; el = el.parentElement) {
    if (el.scrollTop > 0) return true
    if (el === panel) break
  }
  return false
}

/**
 * Hook: swipe-to-close for sheets with live visual feedback.
 *
 * Supports side='right' (swipe right ≥80 px) and side='bottom' (swipe down ≥100 px,
 * and only when the touch starts with the sheet's content scrolled to its top).
 * Returns touch handlers plus a CSS transform string to apply during drag so the
 * sheet follows the user's finger 1:1 with rubber-band damping past the threshold.
 *
 * If `isDirty` is true and the swipe crosses the dismissal threshold,
 * `onDirtyDismissAttempt` is called instead of `onClose`.
 */
export function useSwipeToDismiss(
  side: string | null | undefined,
  onClose: () => void,
  isDirty: boolean,
  onDirtyDismissAttempt: (() => void) | undefined,
) {
  const startX = React.useRef(0)
  const startY = React.useRef(0)
  // Set when the gesture began on a target that owns its own drag (see
  // SWIPE_OPT_OUT_SELECTOR). Latched at touchstart rather than re-tested per
  // move, so a drag that wanders off the slider still belongs to the slider.
  const optedOut = React.useRef(false)
  // The rendered content node — see the data-state effect below.
  const nodeRef = React.useRef<HTMLElement | null>(null)
  // dragOffset drives the live transform — kept in state so the component re-renders
  const [dragOffset, setDragOffset] = React.useState<{ x: number; y: number }>({ x: 0, y: 0 })
  // dragging: finger is down. snapping: a release animation is playing.
  // These gate the inline `transition` so the slide is 1:1 while dragging
  // (no transition) and eased on release (short transition).
  const [dragging, setDragging] = React.useState(false)
  const [snapping, setSnapping] = React.useState(false)
  const closeTimer = React.useRef<number | null>(null)

  React.useEffect(() => () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current)
  }, [])

  /*
   * Put the sheet back at rest every time its content is shown.
   *
   * A clean dismiss parks `dragOffset` a full viewport off-screen so the release
   * animation can play out, and never clears it. That is only safe while closing
   * destroys this hook's state — and it does not always destroy it.
   *
   * Radix gates Content's unmount on the exit animation's `animationend`, while
   * the surrounding Portal unmounts immediately. When that event never arrives
   * (tab backgrounded mid-close, animation interrupted, compositor not ticking)
   * the portal's container is torn out of the document but THIS component
   * instance survives with its state intact. Reopening then mounts a fresh DOM
   * node from the same instance — still translated a full viewport away. The
   * overlay dims and the panel is invisible: the "it won't open the second
   * time" report.
   *
   * Hence a ref callback rather than a mount effect: the surviving instance is
   * handed a NEW node on reopen, so anything that captured the old one (an
   * effect with a [] dep list did exactly that) silently watches a corpse. The
   * observer covers the other shape, where the node is reused and only its
   * data-state flips.
   */
  const observerRef = React.useRef<MutationObserver | null>(null)

  const attachNode = React.useCallback((node: HTMLElement | null) => {
    observerRef.current?.disconnect()
    observerRef.current = null
    nodeRef.current = node
    if (!node) return

    const rest = () => {
      // Clear the node directly as well as the state. The state reset only
      // lands on the NEXT render, and the node has already been painted with
      // the parked transform plus the 220 ms release transition — enough for
      // the browser to start transitioning back from off-screen, which leaves
      // the sheet invisible again for the length of that transition (or
      // indefinitely if the tab is not being ticked).
      // Suppressing the transition across the reflow matters: the base class
      // transitions `transform` over 500 ms, so simply clearing the parked
      // value makes the sheet slide in from off-screen — and a tab that is not
      // being ticked never advances that transition, leaving it invisible for
      // good.
      node.style.transition = 'none'
      node.style.transform = ''
      void node.offsetHeight
      node.style.transition = ''
      // Bail out when already at rest so opening a sheet costs no extra render.
      setDragOffset(prev => (prev.x === 0 && prev.y === 0 ? prev : { x: 0, y: 0 }))
      setSnapping(false)
      setDragging(false)
    }

    if (node.getAttribute('data-state') === 'open') rest()
    const observer = new MutationObserver(() => {
      if (node.getAttribute('data-state') === 'open') rest()
    })
    observer.observe(node, { attributes: true, attributeFilter: ['data-state'] })
    observerRef.current = observer
  }, [])

  React.useEffect(() => () => observerRef.current?.disconnect(), [])

  const onTouchStart = React.useCallback((e: React.TouchEvent) => {
    const target = e.target as HTMLElement | null
    // Both latched for the whole gesture: a scroll that reaches the top mid-drag is
    // still a scroll, so the sheet does not suddenly start following the finger.
    optedOut.current = !!target?.closest?.(SWIPE_OPT_OUT_SELECTOR)
      || (side === 'bottom' && startsInScrolledContent(target, e.currentTarget as HTMLElement))
    if (optedOut.current) return

    startX.current = e.touches[0].clientX
    startY.current = e.touches[0].clientY
    setDragging(true)
    setSnapping(false)
    setDragOffset({ x: 0, y: 0 })
  }, [side])

  const onTouchMove = React.useCallback((e: React.TouchEvent) => {
    if (optedOut.current) return
    const dx = e.touches[0].clientX - startX.current
    const dy = e.touches[0].clientY - startY.current

    if (side === 'bottom' && dy > 0 && dy > Math.abs(dx)) {
      // Rubber-band: 1:1 up to 100 px, then 20 % of additional drag
      const damped = dy > 100 ? 100 + (dy - 100) * 0.2 : dy
      setDragOffset({ x: 0, y: damped })
    } else if (side === 'right' && dx > 0 && dx > Math.abs(dy)) {
      const damped = dx > 80 ? 80 + (dx - 80) * 0.2 : dx
      setDragOffset({ x: damped, y: 0 })
    } else if (side === 'left' && dx < 0 && Math.abs(dx) > Math.abs(dy)) {
      const abs = Math.abs(dx)
      const damped = abs > 80 ? -(80 + (abs - 80) * 0.2) : dx
      setDragOffset({ x: damped, y: 0 })
    }
  }, [side])

  const onTouchEnd = React.useCallback((e: React.TouchEvent) => {
    if (optedOut.current) {
      optedOut.current = false
      return
    }
    setDragging(false)

    const dx = e.changedTouches[0].clientX - startX.current
    const dy = e.changedTouches[0].clientY - startY.current

    let shouldDismiss = false
    if (side === 'bottom' && dy > 100 && dy > Math.abs(dx)) shouldDismiss = true
    if (side === 'right'  && dx > 80  && dx > Math.abs(dy)) shouldDismiss = true
    if (side === 'left'   && dx < -80 && Math.abs(dx) > Math.abs(dy)) shouldDismiss = true

    // Cancelled gesture — ease smoothly back to rest, sheet stays open.
    if (!shouldDismiss) {
      setSnapping(true)
      setDragOffset({ x: 0, y: 0 })
      return
    }

    // Dirty: bounce back and defer to the host's confirm prompt; never close directly.
    if (isDirty && onDirtyDismissAttempt) {
      setSnapping(true)
      setDragOffset({ x: 0, y: 0 })
      onDirtyDismissAttempt()
      return
    }

    // Clean dismiss: slide the rest of the way off-screen, then close once the
    // release animation has played (so it doesn't snap back to 0 first).
    setSnapping(true)
    if (side === 'bottom') setDragOffset({ x: 0, y: window.innerHeight })
    else if (side === 'right') setDragOffset({ x: window.innerWidth, y: 0 })
    else if (side === 'left') setDragOffset({ x: -window.innerWidth, y: 0 })
    closeTimer.current = window.setTimeout(() => onClose(), 220)
  }, [side, isDirty, onClose, onDirtyDismissAttempt])

  const transform: string | undefined =
    dragOffset.y !== 0 ? `translateY(${dragOffset.y}px)` :
    dragOffset.x !== 0 ? `translateX(${dragOffset.x}px)` :
    undefined

  // No transition while the finger drives the offset (1:1 follow); a short eased
  // transition on release; otherwise undefined so the base class governs Radix's
  // own open/close animation.
  const transition: string | undefined =
    dragging ? 'none' : (snapping ? 'transform 220ms ease-out' : undefined)

  return { onTouchStart, onTouchMove, onTouchEnd, transform, transition, attachNode }
}
